import { OwaDate, getISOString } from 'owa-datetime';
import getServiceContextFolderId from 'owa-mail-store/lib/services/utils/getServiceContextFolderId';
import ApplyConversationActionContext from 'owa-mail-store/lib/store/schema/ApplyConversationActionContext';
import ApplyConversationActionRequest from 'owa-service/lib/contract/ApplyConversationActionRequest';
import ApplyConversationActionResponseMessage from 'owa-service/lib/contract/ApplyConversationActionResponseMessage';
import ConversationAction from 'owa-service/lib/contract/ConversationAction';
import applyConversationActionRequest from 'owa-service/lib/factory/applyConversationActionRequest';
import conversationAction from 'owa-service/lib/factory/conversationAction';
import itemId from 'owa-service/lib/factory/itemId';
import applyConversationActionOperation from 'owa-service/lib/operation/applyConversationActionOperation';
import { getJsonRequestHeader } from 'owa-service/lib/Utils';

/**
 * Create the restore conversation action used in request body
 * @param conversationContext the conversation context to be restored
 * @param contextFolderId the contextFolderId
 * @param scheduleReturnTime the time item will return or undefined to unschedule
 * @return the restore conversation action used in request body
 */
function createConversationAction(
    conversationContext: ApplyConversationActionContext,
    contextFolderId: string,
    scheduleReturnTime?: OwaDate | undefined
): ConversationAction {
    return conversationAction({
        Action: scheduleReturnTime ? 'Schedule' : 'Unschedule',
        ConversationId: itemId({ Id: conversationContext.conversationId }),
        ContextFolderId: getServiceContextFolderId(contextFolderId),
        ScheduleReturnTime: scheduleReturnTime ? getISOString(scheduleReturnTime) : null,
    });
}

/**
 * Configure the request body
 * @param conversationContexts the conversation contexts to be deleted
 * @param contextFolderId the contextFolderId
 * @param scheduleReturnTime the time item will return or undefined to unschedule
 * @return the applyConversationActionRequest body
 */
function configureRequestBody(
    conversationContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    scheduleReturnTime?: OwaDate | undefined
): ApplyConversationActionRequest {
    const conversationActions = conversationContexts.map(conversationContext =>
        createConversationAction(conversationContext, contextFolderId, scheduleReturnTime)
    );

    return applyConversationActionRequest({
        ConversationActions: conversationActions,
        ReturnMovedItemIds: true,
    });
}

/**
 * Schedule conversations service
 * @param conversationContexts conversation contexts
 * @param contextFolderId the contextFolderId
 * @param scheduleReturnTime the time item will return or undefined to unschedule
 * @return the ApplyConversationActionResponseMessage
 */
export default function scheduleConversationsService(
    conversationContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    scheduleReturnTime?: OwaDate | undefined
): Promise<ApplyConversationActionResponseMessage> {
    const requestBody = configureRequestBody(
        conversationContexts,
        contextFolderId,
        scheduleReturnTime
    );

    // Work Item 27629: Remove workaround for timezone issue (sending requests in UTC time zone)
    const jsonRequestHeader = getJsonRequestHeader();
    jsonRequestHeader.TimeZoneContext.TimeZoneDefinition.Id = 'UTC';

    return applyConversationActionOperation({
        Header: jsonRequestHeader,
        Body: requestBody,
    }).then(response => {
        return response.Body.ResponseMessages.Items[0];
    });
}
